/**
 * @voltxampere/web — Channel store (Sprint 15)
 *
 * Six oscilloscope slots: CH1-CH4 (probe channels) plus M1-M2
 * (math channels). Per-slot state — enabled flag, probe binding
 * (CH only), expression source (M only), V/div + V offset, and
 * the live sample buffer reference.
 *
 * Sample buffers are owned here so the transient runner pushes
 * into them without importing `<vxa-waveform>`. The waveform
 * component reads via `channels.value.get(id)?.buffer`.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { signal, type Signal } from "@preact/signals-core";
import {
  CHANNEL_IDS,
  SAMPLE_BUFFER_CAP,
  SampleBuffer,
  type ChannelId,
} from "@voltxampere/ui/waveform";

export interface ChannelState {
  readonly id: ChannelId;
  readonly enabled: boolean;
  readonly probeBinding: string | null;
  readonly expression: string | null;
  readonly vDiv: number;
  readonly vOffset: number;
  /** Sample buffer reference. Stable across state mutations so a
   *  channel toggle does not invalidate accumulated samples. */
  readonly buffer: SampleBuffer;
}

function makeInitialState(id: ChannelId): ChannelState {
  return {
    id,
    enabled: id === "ch1", // CH1 enabled out of the box.
    probeBinding: null,
    expression: null,
    vDiv: 1,
    vOffset: 0,
    buffer: new SampleBuffer(SAMPLE_BUFFER_CAP),
  };
}

const initialMap = new Map<ChannelId, ChannelState>();
for (const id of CHANNEL_IDS) initialMap.set(id, makeInitialState(id));

export const channels: Signal<ReadonlyMap<ChannelId, ChannelState>> = signal(initialMap);

function mutate(id: ChannelId, patch: Partial<ChannelState>): void {
  const current = channels.value;
  const existing = current.get(id);
  if (!existing) return;
  const next = new Map(current);
  next.set(id, { ...existing, ...patch });
  channels.value = next;
}

export function bindProbe(id: ChannelId, netId: string): void {
  if (id === "m1" || id === "m2") return; // math channels do not bind to nets
  mutate(id, { probeBinding: netId, enabled: true });
}

export function unbindProbe(id: ChannelId): void {
  mutate(id, { probeBinding: null });
}

export function setMathExpression(id: "m1" | "m2", source: string): void {
  mutate(id, { expression: source, enabled: source.length > 0 });
}

export function setVDiv(id: ChannelId, vDiv: number): void {
  if (!Number.isFinite(vDiv) || vDiv <= 0) return;
  mutate(id, { vDiv });
}

export function setVOffset(id: ChannelId, offset: number): void {
  if (!Number.isFinite(offset)) return;
  mutate(id, { vOffset: offset });
}

export function toggleChannel(id: ChannelId): void {
  const existing = channels.value.get(id);
  if (!existing) return;
  mutate(id, { enabled: !existing.enabled });
}

export function getActiveChannels(): readonly ChannelState[] {
  const out: ChannelState[] = [];
  for (const id of CHANNEL_IDS) {
    const s = channels.value.get(id);
    if (s?.enabled) out.push(s);
  }
  return out;
}

export function getFirstAvailableProbeChannel(): ChannelId | null {
  for (const id of ["ch1", "ch2", "ch3", "ch4"] as const) {
    const s = channels.value.get(id);
    if (s?.probeBinding === null) return id;
  }
  return null;
}

/** Test cleanup — reset to factory defaults + drop sample buffers. */
export function resetChannels(): void {
  const fresh = new Map<ChannelId, ChannelState>();
  for (const id of CHANNEL_IDS) fresh.set(id, makeInitialState(id));
  channels.value = fresh;
}
